import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { 
  ArrowLeft,
  HardHat, 
  MapPin, 
  CheckCircle, 
  Calendar,
  Star,
  MessageCircle,
  Send,
  Phone,
  Mail,
  Building2,
  Shield,
  Award,
  Building
} from 'lucide-react'
import apiService from '../../services/api'
import { toast } from 'react-toastify'
import VendorRatings from '../../components/VendorRatings'

interface VendorDetail {
  id: string
  companyName: string
  certifications: string[] | null
  firstName: string
  lastName: string
  email: string
  avatar: string | null
  location: {
    city: string
    state: string
  }
  contact: {
    phone: string
  }
  governmentVendor: {
    isGovCertified: boolean
    cageCode: string | null
    dunsNumber: string | null
    gsaScheduleId: string | null
    certifications: string[] | null
    approvedAgencies: string[] | null
    govCertifications: string[] | null
    govStatus: string | null
  }
  stats: {
    completedProjects: number
    activeListings: number
    memberSince: string
    govContractsCompleted: number
  }
  recentProjects: Array<{
    id: string
    title: string
    description: string
    completedAt: string
  }>
  listings: Array<{
    id: string
    title: string
    description: string
    status: string
  }>
}



interface QuoteRequest {
  projectType: string
  systemSizeKw: number
  location: string
  budget: number
  timeline: string
  description: string
  documents: File[]
}

export default function GovVendorDetailPage() {
  const { vendorId } = useParams<{ vendorId: string }>()
  
  const [vendor, setVendor] = useState<VendorDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'projects' | 'documents' | 'contact'>('overview')
  
  // Message state
  const [newMessage, setNewMessage] = useState('')
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  
  // Quote request state
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [quoteRequest, setQuoteRequest] = useState<QuoteRequest>({
    projectType: '',
    systemSizeKw: 0,
    location: '',
    budget: 0,
    timeline: '',
    description: '',
    documents: []
  })
  const [sendingQuote, setSendingQuote] = useState(false)
  const [quoteErrors, setQuoteErrors] = useState<{[key: string]: string}>({})

  useEffect(() => {
    if (vendorId) {
      fetchVendorDetails()
    }
  }, [vendorId])

  const fetchVendorDetails = async () => {
    try {
      setLoading(true)
      const response = await apiService.get(`/api/gov/vendor/${vendorId}`) as any
      if (response.success) {
        setVendor(response.data)
      } else {
        setError('Failed to fetch vendor details')
      }
    } catch (err) {
      setError('Failed to fetch vendor details')
      console.error('Error fetching vendor details:', err)
    } finally {
      setLoading(false)
    }
  }

  const validateQuoteRequest = () => {
    const errors: {[key: string]: string} = {}
    
    if (!quoteRequest.projectType.trim()) {
      errors.projectType = 'Project type is required'
    }
    if (quoteRequest.systemSizeKw <= 0) {
      errors.systemSizeKw = 'System size must be greater than 0'
    }
    if (!quoteRequest.location.trim()) {
      errors.location = 'Location is required'
    }
    if (quoteRequest.budget <= 0) {
      errors.budget = 'Budget must be greater than 0'
    }
    if (!quoteRequest.timeline.trim()) {
      errors.timeline = 'Timeline is required'
    }
    if (!quoteRequest.description.trim()) {
      errors.description = 'Description is required'
    }
    
    setQuoteErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      toast.error('Please enter a message')
      return
    }
    
    try {
      setSendingMessage(true)
      // TODO: Implement message sending API call
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API call
      toast.success('Message sent successfully')
      setNewMessage('')
      setShowMessageModal(false)
    } catch (error) {
      console.error('Error sending message:', error)
      toast.error('Failed to send message')
    } finally {
      setSendingMessage(false)
    }
  }

  const handleRequestQuote = async () => {
    if (!validateQuoteRequest()) {
      toast.error('Please fix the errors in the form')
      return
    }
    
    try {
      setSendingQuote(true)
      // TODO: Implement quote request API call
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API call
      toast.success('Quote request sent successfully')
      setShowQuoteModal(false)
      setQuoteRequest({
        projectType: '',
        systemSizeKw: 0,
        location: '',
        budget: 0,
        timeline: '',
        description: '',
        documents: []
      })
      setQuoteErrors({})
    } catch (error) {
      console.error('Error sending quote request:', error)
      toast.error('Failed to send quote request')
    } finally {
      setSendingQuote(false)
    }
  }



  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading vendor details...</p>
        </div>
      </div>
    )
  }

  if (error || !vendor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Vendor not found'}</p>
          <Link 
            to="/gov/dashboard"
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4 mb-6">
            <Link 
              to="/gov/dashboard"
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>

          {/* Vendor Header */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start space-x-6 mb-6 lg:mb-0">
              <div className="w-24 h-24 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                {vendor.avatar ? (
                  <img
                    src={vendor.avatar}
                    alt={vendor.companyName}
                    className="w-24 h-24 rounded-xl object-cover"
                  />
                ) : (
                  <HardHat className="h-12 w-12 text-primary-600" />
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {vendor.companyName}
                </h1>
                <div className="flex items-center text-gray-600 mb-3">
                  <MapPin className="h-5 w-5 mr-2" />
                  <span>{vendor.location.city}, {vendor.location.state}</span>
                </div>
                <div className="flex items-center space-x-6 text-sm text-gray-600">
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                    <span>{vendor.stats.completedProjects} completed jobs</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>Member since {formatDate(vendor.stats.memberSince)}</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-4 w-4 text-yellow-500 mr-1" />
                    <span>4.8 rating</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3 lg:flex-row lg:space-y-0 lg:space-x-3">
              <button
                onClick={() => setShowMessageModal(true)}
                className="flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                Contact Vendor
              </button>
              <button
                onClick={() => setShowQuoteModal(true)}
                className="flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Send className="h-5 w-5 mr-2" />
                Request Quote
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: '📋' },
              { id: 'projects', label: 'Projects', icon: '🏗️' },
              { id: 'documents', label: 'Documents', icon: '📄' },
              { id: 'contact', label: 'Contact', icon: '📞' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-100 text-primary-700 border-b-2 border-primary-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* About */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
                <p className="text-gray-600 leading-relaxed">
                  {vendor.companyName} is a verified solar enterprise vendor with {vendor.stats.completedProjects} completed projects. 
                  {vendor.governmentVendor.isGovCertified && ' This vendor is certified for government contracts.'}
                </p>
              </div>

              {/* Vendor Ratings & Reviews */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Reviews</h2>
                <VendorRatings vendorId={vendor.id} showReviews={true} maxReviews={3} />
              </div>

              {/* Government Certifications */}
              {vendor.governmentVendor.isGovCertified && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Shield className="h-6 w-6 text-emerald-600 mr-2" />
                    Government Certifications
                  </h2>
                  <div className="space-y-4">
                    {vendor.governmentVendor.cageCode && (
                      <div className="flex items-center">
                        <Building className="h-5 w-5 text-emerald-600 mr-3" />
                        <div>
                          <p className="font-medium text-gray-900">CAGE Code</p>
                          <p className="text-gray-600">{vendor.governmentVendor.cageCode}</p>
                        </div>
                      </div>
                    )}
                    {vendor.governmentVendor.dunsNumber && (
                      <div className="flex items-center">
                        <Building2 className="h-5 w-5 text-emerald-600 mr-3" />
                        <div>
                          <p className="font-medium text-gray-900">DUNS Number</p>
                          <p className="text-gray-600">{vendor.governmentVendor.dunsNumber}</p>
                        </div>
                      </div>
                    )}
                    {vendor.governmentVendor.gsaScheduleId && (
                      <div className="flex items-center">
                        <Award className="h-5 w-5 text-emerald-600 mr-3" />
                        <div>
                          <p className="font-medium text-gray-900">GSA Schedule ID</p>
                          <p className="text-gray-600">{vendor.governmentVendor.gsaScheduleId}</p>
                        </div>
                      </div>
                    )}
                    {vendor.governmentVendor.certifications && vendor.governmentVendor.certifications.length > 0 && (
                      <div>
                        <p className="font-medium text-gray-900 mb-2">Certifications</p>
                        <div className="flex flex-wrap gap-2">
                          {vendor.governmentVendor.certifications.map((cert, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-emerald-100 text-emerald-800"
                            >
                              {cert}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {vendor.governmentVendor.approvedAgencies && vendor.governmentVendor.approvedAgencies.length > 0 && (
                      <div>
                        <p className="font-medium text-gray-900 mb-2">Approved Agencies</p>
                        <div className="flex flex-wrap gap-2">
                          {vendor.governmentVendor.approvedAgencies.map((agency, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                            >
                              {agency}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recent Projects */}
              {vendor.recentProjects.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Projects</h2>
                  <div className="space-y-4">
                    {vendor.recentProjects.map((project) => (
                      <div key={project.id} className="border border-gray-200 rounded-lg p-4">
                        <h3 className="font-medium text-gray-900 mb-1">{project.title}</h3>
                        <p className="text-gray-600 text-sm mb-2">{project.description}</p>
                        <p className="text-gray-500 text-xs">Completed {formatDate(project.completedAt)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-gray-600">{vendor.email}</span>
                  </div>
                  {vendor.contact.phone && (
                    <div className="flex items-center">
                      <Phone className="h-5 w-5 text-gray-400 mr-3" />
                      <span className="text-gray-600">{vendor.contact.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-gray-600">{vendor.location.city}, {vendor.location.state}</span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Completed Projects</span>
                    <span className="font-medium text-gray-900">{vendor.stats.completedProjects}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active Listings</span>
                    <span className="font-medium text-gray-900">{vendor.stats.activeListings}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gov Contracts</span>
                    <span className="font-medium text-gray-900">{vendor.stats.govContractsCompleted}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Member Since</span>
                    <span className="font-medium text-gray-900">{formatDate(vendor.stats.memberSince)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Projects</h2>
            {vendor.recentProjects.length > 0 ? (
              <div className="space-y-4">
                {vendor.recentProjects.map((project) => (
                  <div key={project.id} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-1">{project.title}</h3>
                    <p className="text-gray-600 text-sm mb-2">{project.description}</p>
                    <p className="text-gray-500 text-xs">Completed {formatDate(project.completedAt)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No recent projects available.</p>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Documents & Certifications</h2>
            {vendor.governmentVendor.isGovCertified ? (
              <div className="space-y-4">
                {vendor.governmentVendor.certifications && vendor.governmentVendor.certifications.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Certifications</h3>
                    <div className="flex flex-wrap gap-2">
                      {vendor.governmentVendor.certifications.map((cert, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-emerald-100 text-emerald-800"
                        >
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {vendor.governmentVendor.govCertifications && vendor.governmentVendor.govCertifications.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Government Certifications</h3>
                    <div className="flex flex-wrap gap-2">
                      {vendor.governmentVendor.govCertifications.map((cert, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                        >
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No government certifications available.</p>
            )}
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="space-y-4">
              <div className="flex items-center">
                <Mail className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Email</p>
                  <p className="text-gray-600">{vendor.email}</p>
                </div>
              </div>
              {vendor.contact.phone && (
                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">Phone</p>
                    <p className="text-gray-600">{vendor.contact.phone}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center">
                <MapPin className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Location</p>
                  <p className="text-gray-600">{vendor.location.city}, {vendor.location.state}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Send Message to {vendor.companyName}</h3>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg resize-none"
                rows={4}
                placeholder="Type your message here..."
                disabled={sendingMessage}
              />
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={sendingMessage}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={sendingMessage || !newMessage.trim()}
                >
                  {sendingMessage ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quote Request Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Request Quote from {vendor.companyName}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Type *</label>
                  <input
                    type="text"
                    value={quoteRequest.projectType}
                    onChange={(e) => {
                      setQuoteRequest({...quoteRequest, projectType: e.target.value})
                      if (quoteErrors.projectType) {
                        setQuoteErrors({...quoteErrors, projectType: ''})
                      }
                    }}
                    className={`w-full p-3 border rounded-lg ${
                      quoteErrors.projectType ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Solar Installation"
                    disabled={sendingQuote}
                  />
                  {quoteErrors.projectType && (
                    <p className="text-red-500 text-xs mt-1">{quoteErrors.projectType}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">System Size (kW) *</label>
                  <input
                    type="number"
                    value={quoteRequest.systemSizeKw}
                    onChange={(e) => {
                      setQuoteRequest({...quoteRequest, systemSizeKw: Number(e.target.value)})
                      if (quoteErrors.systemSizeKw) {
                        setQuoteErrors({...quoteErrors, systemSizeKw: ''})
                      }
                    }}
                    className={`w-full p-3 border rounded-lg ${
                      quoteErrors.systemSizeKw ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 50"
                    disabled={sendingQuote}
                  />
                  {quoteErrors.systemSizeKw && (
                    <p className="text-red-500 text-xs mt-1">{quoteErrors.systemSizeKw}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                  <input
                    type="text"
                    value={quoteRequest.location}
                    onChange={(e) => {
                      setQuoteRequest({...quoteRequest, location: e.target.value})
                      if (quoteErrors.location) {
                        setQuoteErrors({...quoteErrors, location: ''})
                      }
                    }}
                    className={`w-full p-3 border rounded-lg ${
                      quoteErrors.location ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., City, State"
                    disabled={sendingQuote}
                  />
                  {quoteErrors.location && (
                    <p className="text-red-500 text-xs mt-1">{quoteErrors.location}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget Range *</label>
                  <input
                    type="number"
                    value={quoteRequest.budget}
                    onChange={(e) => {
                      setQuoteRequest({...quoteRequest, budget: Number(e.target.value)})
                      if (quoteErrors.budget) {
                        setQuoteErrors({...quoteErrors, budget: ''})
                      }
                    }}
                    className={`w-full p-3 border rounded-lg ${
                      quoteErrors.budget ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 100000"
                    disabled={sendingQuote}
                  />
                  {quoteErrors.budget && (
                    <p className="text-red-500 text-xs mt-1">{quoteErrors.budget}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timeline *</label>
                  <input
                    type="text"
                    value={quoteRequest.timeline}
                    onChange={(e) => {
                      setQuoteRequest({...quoteRequest, timeline: e.target.value})
                      if (quoteErrors.timeline) {
                        setQuoteErrors({...quoteErrors, timeline: ''})
                      }
                    }}
                    className={`w-full p-3 border rounded-lg ${
                      quoteErrors.timeline ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 3-6 months"
                    disabled={sendingQuote}
                  />
                  {quoteErrors.timeline && (
                    <p className="text-red-500 text-xs mt-1">{quoteErrors.timeline}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    value={quoteRequest.description}
                    onChange={(e) => {
                      setQuoteRequest({...quoteRequest, description: e.target.value})
                      if (quoteErrors.description) {
                        setQuoteErrors({...quoteErrors, description: ''})
                      }
                    }}
                    className={`w-full p-3 border rounded-lg resize-none ${
                      quoteErrors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    rows={3}
                    placeholder="Describe your project requirements..."
                    disabled={sendingQuote}
                  />
                  {quoteErrors.description && (
                    <p className="text-red-500 text-xs mt-1">{quoteErrors.description}</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowQuoteModal(false)
                    setQuoteErrors({})
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={sendingQuote}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestQuote}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={sendingQuote}
                >
                  {sendingQuote ? 'Sending...' : 'Request Quote'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 